import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  DataTable,
  EmptyState,
  InlineAlert,
  Loader,
  LoadingButton,
} from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { errorCodeOf, errorMessage } from "src/grading/utils/apiError";
import { isDomain, parseDomains } from "src/grading/utils/domains";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { rosterSourceSummary } from "src/grading/utils/rosterSource";
import { useAppDispatch } from "src/store/hooks";
import {
  useCreateDistrictMutation,
  useListDistrictsQuery,
} from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import { UsTimeZoneSelect } from "./TimeZoneField";

const LICENSE_COLOR = {
  trial: "default",
  active: "success",
  suspended: "error",
} as const;

/** Refusals that belong to the domains field (contracts/district-admin-api.md). */
const DOMAIN_CODES = new Set(["PUBLIC_DOMAIN", "DOMAIN_TAKEN"]);

/** Platform administrators see every district; district staff land on their own. */
const DistrictsPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();
  const { data: districts, isLoading, isError } = useListDistrictsQuery();
  const [createDistrict, { isLoading: creating }] = useCreateDistrictMutation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("America/Chicago");
  const [domains, setDomains] = useState("");
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canCreate = hasPermissions(
    permissions.CREATE_DISTRICTS.action,
    permissions.CREATE_DISTRICTS.subject,
  );

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!canCreate && districts && districts.length === 1)
      history.replace(`/grading/districts/${districts[0].id}`);
  }, [canCreate, districts, history]);

  const submit = async () => {
    setError(null);
    setDomainsError(null);
    const ssoDomains = parseDomains(domains);
    const badDomain = ssoDomains.find((d) => !isDomain(d));
    if (badDomain) {
      setDomainsError(t("district.domainInvalid", { domain: badDomain }));
      return;
    }
    try {
      const district = await createDistrict({
        name: name.trim(),
        timezone,
        ssoDomains,
      }).unwrap();
      setOpen(false);
      setName("");
      setDomains("");
      history.push(`/grading/districts/${district.id}`);
    } catch (e) {
      const message = errorMessage(t, e, t("districts.createError"));
      if (DOMAIN_CODES.has(errorCodeOf(e) ?? "")) setDomainsError(message);
      else setError(message);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <PageHeader
        title={t("districts.title", { defaultValue: "Districts" })}
        actions={
          canCreate ? (
            <Button variant="contained" onClick={() => setOpen(true)}>
              {t("districts.new", { defaultValue: "New district" })}
            </Button>
          ) : undefined
        }
      />
      {isError && (
        <InlineAlert severity="error">
          {t("districts.loadError", {
            defaultValue:
              "Could not load districts. Check your connection and refresh.",
          })}
        </InlineAlert>
      )}
      <Paper sx={{ p: 2, mt: isError ? 2 : 0 }}>
        <DataTable
          aria-label={t("districts.title", { defaultValue: "Districts" })}
          dense
          rows={districts ?? []}
          getRowKey={(d) => d.id}
          onRowClick={(d) => history.push(`/grading/districts/${d.id}`)}
          emptyState={
            <EmptyState
              title={t("districts.empty", {
                defaultValue: "No districts yet.",
              })}
            />
          }
          columns={[
            {
              key: "name",
              header: t("districts.colDistrict", { defaultValue: "District" }),
              render: (d) => d.name,
            },
            {
              key: "license",
              header: t("districts.colLicense", { defaultValue: "License" }),
              render: (d) => (
                <Chip
                  size="small"
                  color={LICENSE_COLOR[d.licenseStatus]}
                  label={t(`district.licenseStatus.${d.licenseStatus}`, {
                    defaultValue: d.licenseStatus,
                  })}
                />
              ),
            },
            {
              key: "sso",
              header: t("districts.colSso", { defaultValue: "SSO domains" }),
              render: (d) => d.ssoDomains.join(", ") || "—",
            },
            {
              key: "roster",
              header: t("districts.colRoster", {
                defaultValue: "Roster source",
              }),
              // FR-035: vendor, or "CSV upload" / "Roster sheet" with the last import date.
              render: (d) =>
                rosterSourceSummary(t, d.rosterSource, i18n.language),
            },
            {
              key: "timezone",
              header: t("districts.colTimezone", { defaultValue: "Time zone" }),
              render: (d) => d.timezone ?? "—",
            },
          ]}
        />
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="new-district-title"
      >
        <DialogTitle id="new-district-title">
          {t("districts.new", { defaultValue: "New district" })}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label={t("districts.name", { defaultValue: "District name" })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            inputProps={{ maxLength: 120 }}
          />
          <UsTimeZoneSelect
            label={t("districts.timezone")}
            value={timezone}
            onChange={setTimezone}
            helperText={t("districts.timezoneHelper")}
            testId="new-district-timezone"
          />
          <TextField
            label={t("districts.emailDomains", {
              defaultValue: "Staff email domains",
            })}
            value={domains}
            onChange={(e) => {
              setDomains(e.target.value);
              setDomainsError(null);
            }}
            error={Boolean(domainsError)}
            helperText={
              domainsError ??
              t("districts.domainsHelper", {
                defaultValue:
                  "Comma-separated, e.g. demo-usd.org. Staff with these domains sign in through the district identity provider.",
              })
            }
          />
          {error && <InlineAlert severity="error">{error}</InlineAlert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            loading={creating}
            disabled={name.trim().length < 2}
            onClick={submit}
            label={t("common.create", { defaultValue: "Create" })}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DistrictsPage;
